import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ApiClient, ApiError } from "../shared/api/client";
import { AssistantApi } from "../shared/api/endpoints";
import type { SessionAuthResponse, UserRead } from "../shared/api/types";
import { appConfig } from "./config";

interface AuthContextValue {
  api: AssistantApi;
  user: UserRead | null;
  session: SessionAuthResponse | null;
  loading: boolean;
  error: string | null;
  devLoginAvailable: boolean;
  loginWithBitrixId: (bitrixUserId: number) => Promise<void>;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  setPassword: (newPassword: string, currentPassword?: string) => Promise<void>;
  retry: () => Promise<void>;
  logout: () => Promise<void>;
}

const apiClient = new ApiClient(appConfig.apiBaseUrl);
const assistantApi = new AssistantApi(apiClient);
const AuthContext = createContext<AuthContextValue | null>(null);

function errorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 401) {
    return "Войдите через Bitrix24 или используйте корпоративную почту.";
  }
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Не удалось выполнить авторизацию";
}

function applySession(
  response: SessionAuthResponse,
  setUser: (user: UserRead) => void,
  setSession: (session: SessionAuthResponse) => void,
): void {
  apiClient.setCsrfToken(response.csrf_token);
  setUser(response.user);
  setSession(response);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserRead | null>(null);
  const [sessionState, setSessionState] = useState<SessionAuthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearSession = useCallback((): void => {
    apiClient.clearCsrfToken();
    setUser(null);
    setSessionState(null);
  }, []);

  const bootstrap = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      applySession(await assistantApi.me(), setUser, setSessionState);
    } catch (authError) {
      clearSession();
      setError(errorMessage(authError));
    } finally {
      setLoading(false);
    }
  }, [clearSession]);

  const loginWithBitrixId = useCallback(async (bitrixUserId: number): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      applySession(await assistantApi.devAuth(bitrixUserId), setUser, setSessionState);
    } catch (authError) {
      clearSession();
      setError(errorMessage(authError));
      throw authError;
    } finally {
      setLoading(false);
    }
  }, [clearSession]);

  const loginWithPassword = useCallback(async (email: string, password: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      applySession(
        await assistantApi.passwordLogin(email, password),
        setUser,
        setSessionState,
      );
    } catch (authError) {
      clearSession();
      setError(
        authError instanceof ApiError && authError.status === 401
          ? "Неверная почта или пароль."
          : errorMessage(authError),
      );
      throw authError;
    } finally {
      setLoading(false);
    }
  }, [clearSession]);

  const setPassword = useCallback(async (
    newPassword: string,
    currentPassword?: string,
  ): Promise<void> => {
    await assistantApi.setPassword(newPassword, currentPassword);
    await bootstrap();
  }, [bootstrap]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await assistantApi.logout();
    } finally {
      clearSession();
      setError(null);
    }
  }, [clearSession]);

  useEffect(() => {
    apiClient.setUnauthorizedHandler(clearSession);
    void bootstrap();
    return () => apiClient.setUnauthorizedHandler(null);
  }, [bootstrap, clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      api: assistantApi,
      user,
      session: sessionState,
      loading,
      error,
      devLoginAvailable: appConfig.allowDevAuth,
      loginWithBitrixId,
      loginWithPassword,
      setPassword,
      retry: bootstrap,
      logout,
    }),
    [
      user,
      sessionState,
      loading,
      error,
      loginWithBitrixId,
      loginWithPassword,
      setPassword,
      bootstrap,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
